import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, MapPin, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GENTLEFLOW_REGIONS, normalizeRegion, storeRegion } from "../lib/gentleflowPlus";
import { localizedCountries } from "../lib/countries";
import { getPlusConfig, savePlusRegion } from "../lib/plusApi";

export default function PlusRegionGate({ open, token, userRegion, onRegionSaved, onClose }) {
  const { t, i18n } = useTranslation();
  const [region, setRegion] = useState(normalizeRegion(userRegion));
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [otherCountry, setOtherCountry] = useState("");

  const otherCountries = useMemo(() => localizedCountries(i18n.language), [i18n.language]);
  const savedCallbackRef = useRef(onRegionSaved);
  savedCallbackRef.current = onRegionSaved;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setError("");
    const savedUserRegion = normalizeRegion(userRegion);
    setRegion(savedUserRegion && savedUserRegion !== "IN" && savedUserRegion !== "DE" ? "OTHER" : savedUserRegion);
    setOtherCountry(savedUserRegion && savedUserRegion !== "IN" && savedUserRegion !== "DE" ? savedUserRegion : "");
    if (savedUserRegion || !token) return () => { cancelled = true; };
    setChecking(true);
    getPlusConfig(token).then(config => {
      if (cancelled) return;
      const saved = normalizeRegion(config?.region);
      if (saved) {
        storeRegion(saved);
        setRegion(saved !== "IN" && saved !== "DE" ? "OTHER" : saved);
        setOtherCountry(saved !== "IN" && saved !== "DE" ? saved : "");
        savedCallbackRef.current?.(saved);
      }
    }).catch(() => {}).finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [open, userRegion, token]);

  if (!open || checking) return null;

  const choose = code => {
    setError("");
    setRegion(code);
    if (code === GENTLEFLOW_REGIONS.OTHER && region !== "OTHER") setOtherCountry("");
  };
  const selectedCountry = region === GENTLEFLOW_REGIONS.OTHER ? otherCountry : region;

  const save = async () => {
    const target = selectedCountry;
    if (!target || saving || !token) return;
    setSaving(true);
    setError("");
    try {
      const saved = await savePlusRegion(target, token);
      const confirmed = normalizeRegion(saved?.region || target);
      if (!confirmed) throw new Error("Invalid region returned by server");
      storeRegion(confirmed);
      await onRegionSaved?.(confirmed);
    } catch (err) {
      setError(err?.response?.data?.detail || t("region.error", "We could not save your region. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const card = (code, label, flag) => {
    const selected = code === "OTHER" ? region === "OTHER" : region === code;
    return <button key={code} type="button" onClick={() => choose(code)} className={`relative w-full rounded-2xl border p-5 text-left transition ${selected ? "border-[#C084A0] bg-[#C084A0]/8 ring-2 ring-[#C084A0]/15" : "border-[#E8E0E4] hover:border-[#C084A0]/50"}`} aria-pressed={selected}>
      {selected && <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#C084A0] text-white flex items-center justify-center"><Check className="w-4 h-4" /></span>}
      <div className="text-2xl mb-2">{flag}</div>
      <div className="font-semibold text-[#332733]">{label}</div>
      {code === "OTHER" && otherCountry && <div className="mt-1 text-sm text-[#6F606C]">{localizedCountries(i18n.language).find(c => c.code === otherCountry)?.name || otherCountry}</div>}
    </button>;
  };

  return <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#2E1D28]/55 backdrop-blur-sm p-4" role="dialog" aria-modal="true" aria-labelledby="plus-region-title">
    <div className="w-full max-w-lg rounded-[2rem] bg-[#FFFDFC] p-6 sm:p-8 shadow-2xl border border-[#F2E4EB]">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-[#FFBE98]/35 flex items-center justify-center"><MapPin className="w-5 h-5 text-[#4A2937]" /></div>
        <div><p className="text-xs uppercase tracking-[0.18em] text-[#C084A0] font-semibold">Gentleflow</p><h2 id="plus-region-title" className="text-2xl font-semibold text-[#332733]">{t("region.title", "Where are you currently located?")}</h2></div>
      </div>
      <p className="text-sm leading-6 text-[#6F606C]">{t("region.subtitle", "Choose your current country so we can show the correct regional experience.")}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {card("IN", t("region.india", "India"), "🇮🇳")}
        {card("DE", t("region.germany", "Germany"), "🇩🇪")}
        {card("OTHER", t("region.other", "Other country"), "🌍")}
      </div>
      {region === "OTHER" && <div className="mt-4">
        <label className="block text-sm font-semibold text-[#332733] mb-2">{t("region.country", "Select your country")}</label>
        <select value={otherCountry} onChange={e => setOtherCountry(e.target.value)} className="w-full rounded-2xl border border-[#E8E0E4] bg-white px-4 py-3 text-sm text-[#332733]">
          <option value="">{t("region.countryPlaceholder", "Choose a country")}</option>
          {otherCountries.map(country => <option key={country.code} value={country.code}>{country.name}</option>)}
        </select>
        <p className="mt-2 text-xs text-[#8A7A85]">{t("region.globalCurrency", "Other countries use USD for Gentleflow Plus.")}</p>
      </div>}
      {error && <p className="mt-4 text-sm text-red-600" role="alert">{error}</p>}
      <div className="mt-6 rounded-2xl bg-[#F8F2F5] px-4 py-3 flex items-start gap-2 text-xs text-[#6F606C]"><ShieldCheck className="w-4 h-4 shrink-0 text-[#C084A0]" />{t("region.security", "Your country controls the currency and regional checkout shown in the app.")}</div>
      <div className={`mt-5 grid gap-2 ${onClose ? "sm:grid-cols-2" : ""}`}><button type="button" disabled={!selectedCountry || saving || !token} onClick={save} className="mt-5 w-full rounded-2xl bg-[#332733] px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-[#241A24] disabled:opacity-50">{saving ? t("region.saving", "Saving…") : t("region.continue", "Continue")}</button>{onClose && <button type="button" disabled={saving} onClick={onClose} className="rounded-2xl border border-[#ead7e3] bg-white px-5 py-3.5 text-sm font-semibold text-[#7a5770]">Cancel</button>}</div>
    </div>
  </div>;
}
